import { v4 as uuid } from 'uuid';
import { assign } from 'lodash';
import { ChangeDetectionStrategy, Component, type OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatDesigns from "@medsurf/flat-designs";
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { ModalControlComponent } from "@medsurf/flat-components";

@Component({
    selector: 'medsurf-copyright-detail',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './copyright-detail.component.html',
    styleUrls: ['./copyright-detail.component.scss'],
    standalone: false
})
export class CopyrightDetailComponent implements OnInit {
  @ViewChild('textInputText', {static: false}) textInputText: FlatDesigns.TextInputFormControlDesign;
  
  isSubmitted = signal(false);
  #copyright: FlatModels.CopyrightEntityModels.Copyright | null;
  copyrightForm = new FormGroup(
    {
      text: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.CopyrightEntityModels.CopyrightSchema.pick({
          text: true,
        })
      ),
      updateOn: 'change',
    }
  );
  readonly FlatModels = FlatModels;
  
  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public navigationControlFacade: FlatFacades.NavigationControlFacade,
              public constantAuthorFacade: FlatFacades.ConstantAuthorFacade,
              public dialogService: DialogService,
              public translate: TranslateService) {
    // Update User
    this.constantAuthorFacade.copyright$.pipe(takeUntilDestroyed()).subscribe((copyright) => {
      this.#copyright = structuredClone(copyright);
      if (this.#copyright) {
        this.copyrightForm.reset(this.#copyright);
      }
      this.isSubmitted.set(false);
    });
  }

  ngOnInit() {
    // Autofocus
    setTimeout(() => {
      this.textInputText?.inputElement?.nativeElement?.focus();
    });
  }

  onSubmit() {
    if (this.isSubmitted() || this.copyrightForm.invalid) {
      return;
    }
    this.isSubmitted.set(true);

    if (this.#copyright) {
      // Update copyright
      const copyright = assign(this.#copyright, this.copyrightForm.value);
      this.constantAuthorFacade.requestUpdateCopyright(copyright).subscribe({
        error: (result) => {
          this.isSubmitted.set(false);
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.copyrightForm);
        },
        next: () => {
          this.isSubmitted.set(false);
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    } else {
      // Add Copyright
      const copyright: FlatModels.CopyrightEntityModels.Copyright = {
        id: uuid(),
        text: this.copyrightForm.value.text || ''
      };
      this.constantAuthorFacade.requestAddCopyright(copyright).subscribe({
        error: (result) => {
          this.isSubmitted.set(false);
          ZodSharedValidator.handleError(result, this.alertControlFacade, this.copyrightForm);
        },
        next: () => {
          this.isSubmitted.set(false);
          this.navigationControlFacade.requestNavigate(['copyright', copyright.id]);
          this.alertControlFacade.requestAddAlert({
            key: 'main',
            detail: 'notification.saved',
            severity: FlatModels.AlertControlModels.AlertType.enum.success,
            life: FlatModels.AlertControlModels.AlertTime.DEFAULT
          });
        }
      });
    }
  }
  
  deleteCopyright() {
    if (!this.#copyright) return;
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.copyright_remove'),
      data: {
        label: 'global.copyright_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (this.#copyright && remove) {
        this.constantAuthorFacade.requestRemoveCopyright(this.#copyright).subscribe({
          error: (result) => {
            ZodSharedValidator.handleError(result, this.alertControlFacade, this.copyrightForm);
          },
          next: () => {
            this.navigationControlFacade.requestNavigate(['copyrights']);
            this.alertControlFacade.requestAddAlert({
              key: 'main',
              detail: 'notification.saved',
              severity: FlatModels.AlertControlModels.AlertType.enum.success,
              life: FlatModels.AlertControlModels.AlertTime.DEFAULT
            });
          }
        });
      }
    });
  }
}
